import React, { useEffect, useState } from "react";
import styled from "styled-components";
import Banner from "common/Banner/Banner";
import { getIsDarkMode } from "utils/misc";
import Text from "common/Typography/Text";
import Link from "common/Typography/Link";
import useMedia from "utils/hooks/useMedia";
import { MOBILE_SEARCH } from "common/constants";
import { WorkspaceType } from "@shared/types";
import useWorkspace from "utils/hooks/useWorkspace";
import { useBottomNavigator, useIsOnboarding } from "utils/hooks/misc";
import { AnimatePresence, motion } from "motion/react";
import DemoTag from "common/Tags/DemoTag";
import { useUser } from "@shared/utils/hooks/misc";

const DemoModeBanner = () => {
  const isDarkMode = getIsDarkMode();
  const isMobile = useMedia([`(max-width: ${MOBILE_SEARCH})`], [true], false);
  const isShortTag = useMedia([`(max-width: 385px)`], [true], false);
  const isOnboarding = useIsOnboarding();
  const { workspace, onWorkspaceChange } = useWorkspace();
  const [showBanner, setShowBanner] = useState(false);
  const { user } = useUser();
  const showAnimation = !user?.isFree();
  const bottomNavigatorData = useBottomNavigator();

  useEffect(() => {
    setTimeout(() => setShowBanner(workspace === WorkspaceType.DEMO), 200);
  }, [workspace]);

  if ((!showAnimation && workspace !== WorkspaceType.DEMO) || isOnboarding)
    return null;

  const props = showAnimation
    ? {
        initial: { opacity: 0 },
        animate: {
          opacity: 1,
          transition: { ease: "easeIn", duration: 0.4 },
        },
        exit: {
          opacity: 0,
          transition: { ease: "easeOut", duration: 0.3 },
        },
      }
    : undefined;

  return (
    <AnimatePresence>
      {(showBanner || !showAnimation) && (
        <motion.div {...props}>
          <BannerWrapper
            $isDarkMode={isDarkMode}
            $isBottomNavigator={bottomNavigatorData.shouldDisplay}
            innerWrapperStyle={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <DemoTag isSmall={isMobile} isShort={isShortTag} />
            <BannerText $isMobile={isMobile}>
              {!bottomNavigatorData.shouldDisplay && "Demo mode active. "}
              To start live trading
              <Link
                style={{ marginLeft: 6 }}
                onClick={() => onWorkspaceChange(WorkspaceType.USER)}
              >
                click here
              </Link>
            </BannerText>
          </BannerWrapper>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default DemoModeBanner;

const BannerWrapper = styled(Banner)<{
  $isDarkMode?: boolean;
  $isBottomNavigator?: boolean;
}>`
  background: ${(p) =>
    p.$isDarkMode
      ? "linear-gradient(90deg, #17191D 0%, #344165 100%)"
      : "linear-gradient(to right, #FFFFFF, #EFF4FF 75%, #3E6CFF51)"};
  height: 54px;
  display: flex;
  align-items: center;
  justify-content: center;
  user-select: none;
  overflow: hidden;
  @media (max-width: ${MOBILE_SEARCH}) {
    padding: 12px 24px;
    height: auto;
  }
  ${(p) =>
    p.$isBottomNavigator &&
    `
      padding: 6px 8px !important;
    `};
`;
const BannerText = styled(Text)<{ $isMobile?: boolean }>`
  margin-left: ${(p) => (p.$isMobile ? 12 : 8)}px;
  text-align: start;
`;
